import { Route } from '@angular/router';
import {
    defaultManageRoutes,
    defaultRouters,
    defaultRouteTemplate,
    defaultWmsRoutes,
    featureRoutes,
    MenuItemFn,
    PlatformRoutes,
    RootRoutesForWms,
    RoutesForManage,
    userPositionResolver,
    UserPositionResolverData,
} from '@pf/shared/ui-app-shared';
import { Injectable, Provider } from '@angular/core';
import { AppRoutesProvider, IAppMenu, IAppSubMenu } from '@pf/shared-ui';
import {
    PermissionTypes,
    PFCoreEntities,
    PFItemCatalogEntities,
    PFLoadEntities,
    PFWmsEntities,
} from '@pf/shared-common';
import {
    CPW_REPORTING_DASHBOARDS,
    CpwCustomPermissions,
    CpwUserPositionTypes,
    RoutesForCycleCounts,
    RoutesForInventory,
    RoutesForPacking,
    RoutesForPicking,
    RoutesForPutaway,
    RoutesForReceiving,
    RoutesForReplenishments,
    RoutesForReporting,
} from '@cpw/domain';
import { UserPermissionGuard } from '@pf/shared/ui-platform';
import {
    buildCorePermissionPath,
    buildItemCatalogPermissionPath,
    buildWmsPermissionPath,
} from '@pf/shared/util-platform';
import { RoutesForException } from '@pf/platform-wms/domain';

export const appRoutes: Route[] = defaultRouteTemplate('queues', [
    {
        path: 'queues',
        loadChildren: () =>
            import('@cpw/feature-queues').then(m => m.QueuesModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Home',
        },
    },
    {
        path: 'receiving',
        loadChildren: () =>
            import('@cpw/feature-receiving').then(m => m.ReceivingModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Receiving',
            expectedPermission: [CpwCustomPermissions.OperationsCanReceive],
            positionType: CpwUserPositionTypes.Receiving,
        } as UserPositionResolverData,
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'picking',
        loadChildren: () =>
            import('@cpw/feature-picking').then(m => m.PickingModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Picking',
            expectedPermission: [CpwCustomPermissions.OperationsCanPick],
        },
    },
    {
        path: 'packing',
        loadChildren: () =>
            import('@cpw/feature-packing').then(m => m.PackingModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Packing',
            expectedPermission: [CpwCustomPermissions.OperationsCanPack],
            positionType: CpwUserPositionTypes.Packing,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'putaway',
        loadChildren: () =>
            import('@cpw/feature-putaway').then(m => m.PutawayModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Put Away',
            expectedPermission: [CpwCustomPermissions.OperationsCanPutAway],
            positionType: CpwUserPositionTypes.Putaway,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'cycle-counts',
        loadChildren: () =>
            import('@cpw/feature-cycle-counts').then(m => m.CycleCountsModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Cycle Counts',
            expectedPermission: [CpwCustomPermissions.OperationsCanCycleCount],
            positionType: CpwUserPositionTypes.CycleCounts,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'replenishments',
        loadChildren: () =>
            import('@cpw/feature-replenishments').then(
                m => m.ReplenishmentsModule
            ),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Replenishments',
            expectedPermission: [CpwCustomPermissions.OperationsCanReplenish],
            positionType: CpwUserPositionTypes.Replenishments,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'exceptions',
        loadChildren: () =>
            import('@cpw/feature-exceptions').then(m => m.ExceptionsModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Exceptions',
            expectedPermission: [
                CpwCustomPermissions.OperationsCanViewExceptions,
                CpwCustomPermissions.OperationsCanManageExceptions,
                CpwCustomPermissions.OperationsExceptionManagerApproval,
            ],
            positionType: CpwUserPositionTypes.Exceptions,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    },
    {
        path: 'shipments',
        loadChildren: () =>
            import('@cpw/feature-shipments').then(m => m.ShipmentsModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Shipments',
            expectedPermission: [
                CpwCustomPermissions.OperationsCanViewShipments,
            ],
        },
    },
    {
        path: 'reporting',
        loadChildren: () =>
            import('@cpw/feature-reporting').then(m => m.ReportingModule),
        canLoad: [UserPermissionGuard],
        data: {
            breadcrumb: 'Reporting',
            expectedPermission: [
                CpwCustomPermissions.ManagementCanViewReportingOptions,
                CpwCustomPermissions.ReportingCanViewAllReports,
            ],
        },
    },
    defaultManageRoutes({
        [PFCoreEntities.Contact]: true,
        [PFCoreEntities.Carrier]: true,
        [PFCoreEntities.Vendor]: true,
        [PFCoreEntities.Customer]: true,
        [PFCoreEntities.Location]: true,
        [PFCoreEntities.User]: true,
        [PFCoreEntities.UserRole]: true,
        [PFCoreEntities.UserPosition]: true,
        [PFCoreEntities.FiscalPeriod]: false,
        [PFItemCatalogEntities.Item]: true,
        [PFLoadEntities.LoadSchedule]: false,
    }),
    defaultWmsRoutes('wms', 'warehouses', [
        {
            path: 'inventory',
            loadChildren: () =>
                import('@cpw/feature-inventory').then(
                    m => m.CpwFeatureInventoryModule
                ),
            canLoad: [UserPermissionGuard],
            data: {
                breadcrumb: 'Inventory',
                expectedPermission: [
                    CpwCustomPermissions.ManagementCanManageWarehouseLocationsAndInventory,
                ],
            },
        },
    ]),
]);

export class CpwRootRoutesForWms extends RootRoutesForWms {
    inventory = this.childRoutes('inventory', RoutesForInventory);
}

@Injectable()
export class AppRouter extends PlatformRoutes {
    manage = this.childRoutes('manage', RoutesForManage);
    wms = this.childRoutes('wms', CpwRootRoutesForWms);
    picking = this.childRoutes('picking', RoutesForPicking);
    exceptions = this.childRoutes('exceptions', RoutesForException);
    replenishments = this.childRoutes(
        'replenishments',
        RoutesForReplenishments
    );
    cycleCounts = this.childRoutes('cycle-counts', RoutesForCycleCounts);
    receiving = this.childRoutes('receiving', RoutesForReceiving);
    reporting = this.childRoutes('reporting', RoutesForReporting);
    packing = this.childRoutes('packing', RoutesForPacking);
    putaway = this.childRoutes('putaway', RoutesForPutaway);

    constructor() {
        super();
        this.setupNavigation();
    }

    queues() {
        return this.url('queues');
    }

    shipments() {
        return this.url('shipments');
    }

    protected getAllMenuItems(): IAppMenu[] {
        return [
            {
                displayName: 'Receiving',
                description: 'Receive POs and shipments',
                category: 'operations',
                icon: 'receiving',
                route: this.receiving.dashboard().url,
                permissions: [CpwCustomPermissions.OperationsCanReceive],
            },
            {
                displayName: 'Put Away',
                description: 'Put away received inventory',
                category: 'operations',
                icon: 'putAway',
                route: this.putaway.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPutAway],
            },
            {
                displayName: 'Picking',
                description: 'Pick orders for fulfillment',
                category: 'operations',
                icon: 'picking',
                route: this.picking.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPick],
            },
            {
                displayName: 'Packing',
                description: 'Pack and label shipments',
                category: 'operations',
                icon: 'packing',
                route: this.packing.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPack],
            },
            {
                displayName: 'Cycle Counts',
                description: 'Count and verify inventory',
                category: 'operations',
                icon: 'cycleCounts',
                route: this.cycleCounts.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanCycleCount],
            },
            {
                displayName: 'Replenishments',
                description: 'Replenish pick locations',
                category: 'operations',
                icon: 'replenishments',
                route: this.replenishments.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanReplenish],
            },
            {
                displayName: 'Shipments',
                description: 'View and manage shipments',
                category: 'operations',
                icon: 'truckCheck',
                route: this.shipments().url,
                permissions: [CpwCustomPermissions.OperationsCanViewShipments],
            },
            {
                displayName: 'Exceptions',
                description: 'Review warehouse exceptions',
                category: 'management',
                icon: 'exceptions',
                route: this.exceptions.main().url,
                permissions: [
                    CpwCustomPermissions.OperationsCanViewExceptions,
                    CpwCustomPermissions.OperationsCanManageExceptions,
                    CpwCustomPermissions.OperationsExceptionManagerApproval,
                ],
            },
            {
                displayName: 'Reporting',
                description: 'Dashboards and analytics',
                category: 'management',
                icon: 'reporting',
                permissions: [
                    CpwCustomPermissions.ManagementCanViewReportingOptions,
                    CpwCustomPermissions.ReportingCanViewAllReports,
                ],
                subMenu: CPW_REPORTING_DASHBOARDS.map(
                    dashboard =>
                        ({
                            displayName: dashboard.displayName,
                            description: dashboard.description,
                            route: this.reporting.dashboard(dashboard.slug).url,
                            group: dashboard.category,
                            permissions: [
                                dashboard.permission,
                                CpwCustomPermissions.ReportingCanViewAllReports,
                            ],
                        }) as IAppSubMenu
                ),
            },
            {
                displayName: 'Receiving',
                icon: 'receiving',
                route: this.receiving.dashboard().url,
                permissions: [CpwCustomPermissions.OperationsCanReceive],
            },
            {
                displayName: 'Picking',
                icon: 'picking',
                route: this.picking.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPick],
            },
            {
                displayName: 'Packing',
                icon: 'packing',
                route: this.packing.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPack],
            },
            {
                displayName: 'Put Away',
                icon: 'putAway',
                route: this.putaway.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanPutAway],
            },
            {
                displayName: 'Cycle Counts',
                icon: 'cycleCounts',
                route: this.cycleCounts.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanCycleCount],
            },
            {
                displayName: 'Replenishments',
                icon: 'replenishments',
                route: this.replenishments.queue().url,
                permissions: [CpwCustomPermissions.OperationsCanReplenish],
            },
            {
                displayName: 'Exceptions',
                icon: 'exceptions',
                route: this.exceptions.main().url,
                permissions: [
                    CpwCustomPermissions.OperationsCanViewExceptions,
                    CpwCustomPermissions.OperationsCanManageExceptions,
                    CpwCustomPermissions.OperationsExceptionManagerApproval,
                ],
            },
            {
                displayName: 'Shipments',
                icon: 'truckCheck',
                route: this.shipments().url,
                permissions: [CpwCustomPermissions.OperationsCanViewShipments],
            },
            {
                displayName: 'Manage Core Data',
                description: 'Customers, vendors, and items',
                category: 'management',
                icon: 'platformCore',
                permissions: [
                    CpwCustomPermissions.ManagementCanViewManageOptions,
                ],
                subMenu: [
                    {
                        ...featureRoutes[PFCoreEntities.Vendor].menu(
                            this.manage.vendors
                        ),
                        description: 'Suppliers and partners',
                    },
                    {
                        ...featureRoutes[PFCoreEntities.Carrier].menu(
                            this.manage.carriers
                        ),
                        description: 'Freight providers',
                    },
                    {
                        ...featureRoutes[PFCoreEntities.Location].menu(
                            this.manage.locations,
                            [
                                buildCorePermissionPath(
                                    PermissionTypes.Admin,
                                    PFCoreEntities.Location
                                ),
                            ]
                        ),
                        description: 'Sites and addresses',
                    },
                    {
                        ...featureRoutes[PFCoreEntities.Contact].menu(
                            this.manage.contacts,
                            [
                                buildCorePermissionPath(
                                    PermissionTypes.Admin,
                                    PFCoreEntities.Contact
                                ),
                            ]
                        ),
                        description: 'People and accounts',
                    },
                    {
                        ...featureRoutes[PFItemCatalogEntities.Item].menu(
                            this.manage.itemCatalog,
                            [
                                buildItemCatalogPermissionPath(
                                    PermissionTypes.Admin,
                                    PFItemCatalogEntities.Item
                                ),
                            ]
                        ),
                        description: 'Catalog and SKUs',
                    },
                    {
                        ...featureRoutes[PFCoreEntities.User].menu(
                            this.manage.users
                        ),
                        description: 'Accounts and access',
                    },
                    MenuItemFn(
                        'Roles & Permissions',
                        'Permission groups'
                    )(this.manage.roles),
                    {
                        ...featureRoutes[PFCoreEntities.UserPosition].menu(
                            this.manage.userPositions,
                            [
                                buildCorePermissionPath(
                                    PermissionTypes.Admin,
                                    PFCoreEntities.UserPosition
                                ),
                            ]
                        ),
                        description: 'Roles within sites',
                    },
                ],
            },
            {
                displayName: 'Manage Warehouse Data',
                description: 'Zones, slots, and equipment',
                category: 'management',
                icon: 'platformWms',
                permissions: [
                    CpwCustomPermissions.ManagementCanViewWarehouseOptions,
                ],
                subMenu: [
                    {
                        ...featureRoutes[PFWmsEntities.Warehouse].menu(
                            this.wms.warehouses,
                            [
                                buildWmsPermissionPath(
                                    PermissionTypes.Admin,
                                    PFWmsEntities.Warehouse
                                ),
                            ]
                        ),
                        description: 'Facilities setup',
                    },
                    {
                        displayName: 'Zones, Slots, Handling Units',
                        description: 'Storage structure',
                        permissions: [
                            'Wms:Zones:Modify',
                            'Wms:Slots:Modify',
                            CpwCustomPermissions.ManagementCanManageWarehouseLocationsAndInventory,
                        ],
                        route: this.wms.warehouses.structure('active').url,
                    },
                    {
                        displayName: 'Inventory',
                        description: 'On-hand and adjustments',
                        permissions: [
                            CpwCustomPermissions.ManagementCanManageWarehouseLocationsAndInventory,
                        ],
                        route: this.wms.inventory.manage().url,
                    },
                    {
                        displayName: 'Manage Will-Calls',
                        description: 'Customer pickups',
                        permissions: [
                            CpwCustomPermissions.ManagementCanSetWillCall,
                        ],
                        route: this.picking.willCall().url,
                    },
                    {
                        displayName: 'Manage Hot-Shots',
                        description: 'Priority shipments',
                        permissions: [
                            CpwCustomPermissions.ManagementCanSetHotShot,
                        ],
                        route: this.picking.hotShot().url,
                    },
                ],
            },
        ];
    }
}

export const appRouteProviders = [
    ...defaultRouters,
    AppRouter,
    CpwRootRoutesForWms,
    RoutesForPicking,
    RoutesForReplenishments,
    RoutesForCycleCounts,
    RoutesForReceiving,
    RoutesForReporting,
    RoutesForPacking,
    RoutesForPutaway,
    RoutesForInventory,
    { provide: AppRoutesProvider, useClass: AppRouter },
] as Provider[];
